import { useRef, useState } from "react";

interface UseSpeechRecognitionProps {
  onConfirm: (transcript: string) => void;
}

export const useSpeechRecognition = ({
  onConfirm,
}: UseSpeechRecognitionProps) => {
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState("");
  const recognitionRef = useRef<any>(null);
  const cumulativeTextRef = useRef("");
  const interimTextRef = useRef("");
  const isEngineActiveRef = useRef(false);
  const resumeRequestedRef = useRef(false);
  const isPausedRef = useRef(false);

  const createRecognition = () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) return null;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => {
      isEngineActiveRef.current = true;
    };

    recognition.onerror = () => {
      isEngineActiveRef.current = false;
      setIsListening(false);
      setIsPaused(false);
      isPausedRef.current = false;
    };

    recognition.onend = () => {
      isEngineActiveRef.current = false;

      if (resumeRequestedRef.current) {
        resumeRequestedRef.current = false;
        const next = createRecognition();
        if (next) {
          recognitionRef.current = next;
          try {
            next.start();
            return;
          } catch (error) {
            console.error("Failed to resume speech recognition:", error);
          }
        }
        setIsListening(false);
        setIsPaused(false);
        isPausedRef.current = false;
        return;
      }

      if (!isPausedRef.current) {
        setIsListening(false);
      }
    };

    recognition.onresult = (event: any) => {
      if (isPausedRef.current) {
        return;
      }

      let newFinalText = "";
      let newInterimText = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const resultText = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          newFinalText += resultText;
        } else {
          newInterimText += resultText;
        }
      }

      if (newFinalText) {
        cumulativeTextRef.current += newFinalText;
      }

      interimTextRef.current = newInterimText;

      const combined = cumulativeTextRef.current + interimTextRef.current;
      setLiveTranscript(combined);
    };

    return recognition;
  };

  const startListening = () => {
    const recognition = createRecognition();
    if (!recognition) return;

    cumulativeTextRef.current = "";
    interimTextRef.current = "";
    setLiveTranscript("");
    setIsPaused(false);
    isPausedRef.current = false;
    resumeRequestedRef.current = false;
    recognitionRef.current = recognition;

    try {
      recognition.start();
      setIsListening(true);
    } catch (error) {
      console.error("Failed to start speech recognition:", error);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  const confirmListening = () => {
    setIsListening(false);
    setIsPaused(false);
    isPausedRef.current = false;
    resumeRequestedRef.current = false;
    const transcript = cumulativeTextRef.current + interimTextRef.current;

    if (transcript) {
      onConfirm(transcript);
    }
    stopListening();

    cumulativeTextRef.current = "";
    interimTextRef.current = "";
    setLiveTranscript("");
  };

  const cancelListening = () => {
    setIsListening(false);
    setIsPaused(false);
    isPausedRef.current = false;
    resumeRequestedRef.current = false;

    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }

    cumulativeTextRef.current = "";
    interimTextRef.current = "";
    setLiveTranscript("");
  };

  const pauseListening = () => {
    if (recognitionRef.current && isListening && !isPaused) {
      cumulativeTextRef.current += interimTextRef.current;
      interimTextRef.current = "";

      setIsPaused(true);
      isPausedRef.current = true;
      recognitionRef.current.stop();
      setLiveTranscript(cumulativeTextRef.current);
    }
  };

  const resumeListening = () => {
    if (!isPaused) return;

    interimTextRef.current = "";
    setIsPaused(false);
    isPausedRef.current = false;

    if (isEngineActiveRef.current) {
      resumeRequestedRef.current = true;
      return;
    }

    const recognition = createRecognition();
    if (!recognition) return;
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setLiveTranscript(cumulativeTextRef.current);
      setIsListening(true);
    } catch (error) {
      console.error("Failed to resume speech recognition:", error);
      setIsPaused(true);
      isPausedRef.current = true;
    }
  };

  const toggleListening = () => {
    if (isListening) {
      confirmListening();
    } else {
      startListening();
    }
  };

  return {
    isListening,
    isPaused,
    liveTranscript,
    startListening,
    stopListening,
    toggleListening,
    confirmListening,
    cancelListening,
    pauseListening,
    resumeListening,
  };
};
